import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'components/Toast';
import { api } from 'utils/api';
import { calendarDate, toastError } from 'utils/helpers';
import {
  IEscriptAmendOrderProp,
  IEscriptCartResponse,
  IEscriptCheckoutProp,
  IEscriptDispenseResponse,
  IEscriptLifestyle,
  IEscriptOpenOrderResponse,
  IEscriptOrderDataResponse,
  IEscriptOrdersHistoryResponse,
  IEscriptOrdersResponse,
  IEscriptPrescription,
  IEscriptPrescriptionResponse,
  IEscriptProductsResponse,
  IEscriptScriptProductsResponse,
  IEscriptSupplement,
  IEscriptSupplementsResponse,
  IOrderHeaderData,
  IOrderHeaderDataEntries,
  IOrderHistoryResponse,
  IPatientCart,
  IPatientEscriptsResponse,
  IPharmacy,
  IPrintOrder,
  ISelectOptions
} from 'utils/models';
import { TCreateOrderInvoice } from 'utils/types';

interface IGetProductProp {
  page: number;
  perPage: number;
  isActive: boolean;
  search?: string;
}

interface IGetSupplementsProp {
  page: number;
  perPage: number;
  isActive: boolean;
  search?: string;
}

interface IPrescriptionProp extends IEscriptParamProp {
  tokenStatus: string;
}

interface IOrderProp extends IEscriptParamProp {
  status: string;
  isPaid: string;
}

interface IDispenseProp extends IEscriptParamProp {
  status: string;
}

interface IEscriptParamProp {
  page: number;
  perPage: number;
  search?: string;
  date: {
    from: Date | null;
    to: Date | null;
  };
}

export interface IShippingAddress {
  street: string;
  city: string;
  state: string;
  postcode: string;
}

const API_URL = '/escripts';

/**
 * GET OPEN ORDERS
 * @param param0
 * @returns
 */
export const getOpenOrders = async ({
  patientId,
  isAmend = '0'
}: {
  patientId: string;
  isAmend: '1' | '0';
}): Promise<IEscriptOpenOrderResponse> => {
  const { data } = await api.get(`${API_URL}/${patientId}/patient/available?amendment=${isAmend}`);

  return data;
};

export const useGetOpenOrders = ({
  patientId,
  isAmend = '0'
}: {
  patientId: string;
  isAmend: '1' | '0';
}) => {
  return useQuery({
    queryKey: ['escriptsOpenOrder', patientId],
    queryFn: () => getOpenOrders({ patientId, isAmend }),
    initialData: { message: null, status: 200, count: 0, data: [] }
  });
};

/**
 * GET PRESCRIPTIONS
 * @param param0
 * @returns
 */
export const getPrescriptions = async ({
  page,
  perPage,
  tokenStatus,
  search,
  date
}: IPrescriptionProp): Promise<IEscriptPrescriptionResponse> => {
  const searchStart = date.from ? calendarDate({ date: date.from }).fromUtc : null;
  const searchEnd = date.to ? calendarDate({ date: date.to }).toUtc : null;

  let apiUrl = `${API_URL}?page=${page}&perPage=${perPage}&tokenStatus=${tokenStatus}`;
  if (searchStart && searchEnd) apiUrl += `&dateFrom=${searchStart}&dateTo=${searchEnd}`;
  if (search) apiUrl += `&search=${search}`;
  const { data }: { data: IEscriptPrescriptionResponse } = await api.get(apiUrl);

  return data;
};

export const useGetPrescriptions = ({
  page,
  perPage,
  tokenStatus,
  search,
  date
}: IPrescriptionProp) => {
  return useQuery({
    queryKey: ['escriptsPrescription', page, tokenStatus, search, perPage, date],
    queryFn: () => getPrescriptions({ page, perPage, tokenStatus, search, date }),
    initialData: { message: null, status: 200, count: 0, data: [] }
  });
};

/**
 * GET ORDERS
 * @param param0
 * @returns
 */
export const getOrders = async ({
  page,
  perPage,
  search,
  date,
  status,
  isPaid
}: IOrderProp): Promise<IEscriptOrdersResponse> => {
  // const searchStart = date.from ? calendarDate({ date: date.from }).fromUtc : null;
  // const searchEnd = date.to ? calendarDate({ date: date.to }).toUtc : null;

  let apiUrl = `${API_URL}/orders?page=${page}&perPage=${perPage}`;

  // let apiUrl = `${API_URL}/orders`;

  // if (searchStart && searchEnd) apiUrl += `&dateFrom=${searchStart}&dateTo=${searchEnd}`;
  if (search) apiUrl += `&search=${search}`;
  if (status) apiUrl += `&status=${status}`;
  // if (isPaid) apiUrl += `&isPaid=${+isPaid}`;

  const { data } = await api.get(apiUrl);

  return data;
};

export const useGetOrders = ({ page, perPage, date, search, status, isPaid }: IOrderProp) => {
  return useQuery({
    queryKey: ['escriptsOrders', page, perPage, date, search, status, isPaid],
    queryFn: () => getOrders({ page, perPage, date, search, status, isPaid }),
    initialData: { message: null, status: 200, count: 0, data: [] }
  });
};

/**
 * DISPENSE PRODUCTS
 * @param param0
 * @returns
 */
export const getDispense = async ({
  page,
  perPage,
  search,
  date,
  status
}: IDispenseProp): Promise<IEscriptDispenseResponse> => {
  const searchStart = date.from ? calendarDate({ date: date.from }).fromUtc : null;
  const searchEnd = date.to ? calendarDate({ date: date.to }).toUtc : null;

  let apiUrl = `${API_URL}/dispensing?page=${page}&perPage=${perPage}`;

  if (searchStart && searchEnd) apiUrl += `&dateFrom=${searchStart}&dateTo=${searchEnd}`;
  if (search) apiUrl += `&search=${search}`;
  if (status) apiUrl += `&status=${status}`;

  const { data } = await api.get(apiUrl);

  return data;
};

export const useGetDispense = ({ page, perPage, date, search, status }: IDispenseProp) => {
  return useQuery({
    queryKey: ['escriptsDispense', page, perPage, date, search, status],
    queryFn: () => getDispense({ page, perPage, date, search, status }),
    initialData: { message: null, status: 200, count: 0, data: [] }
  });
};

/**
 * MARK AS DISPENSE
 * @param id
 * @returns
 */
export const dispenseProduct = async (payload: { id: string; type: string }) => {
  const { data: result } = await api.post(`${API_URL}/${payload.id}/dispense`, {
    type: payload.type
  });
  return result;
};

export const useDispenseProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { id: string; type: string }) => dispenseProduct(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['escriptsDispense'] });
      queryClient.invalidateQueries({ queryKey: ['escriptsOrders'] });
    },
    onError: (error: Error) => toastError(error)
  });
};

/**
 * CANCEL ORDER
 * @param id
 * @returns
 */
export const cancelOrder = async (id: string) => {
  const { data: result } = await api.post(`${API_URL}/${id}/order/cancel`);
  return result;
};

export const useCancelOrder = () => {
  return useMutation({
    mutationFn: (id: string) => cancelOrder(id),
    onError: (error) => toastError(error)
  });
};

/**
 * CANCEL DISPENSE
 * @param id
 * @returns
 */
export const cancelDispense = async (id: string) => {
  const { data: result } = await api.post(`${API_URL}/dispense/${id}/cancel`);
  return result;
};

export const useCancelDispense = () => {
  return useMutation({
    mutationFn: (id: string) => cancelDispense(id),
    onError: (error) => toastError(error)
  });
};

/**
 * GENERATE ESCRIPT - CONSULTATIONS
 * @param consultationId
 * @returns
 */
export const generateEscript = async (consultationId: string) => {
  const { data: result } = await api.post(`${API_URL}/${consultationId}/generate`);
  return result;
};

export const useGenerateEscript = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (consultationId: string) => generateEscript(consultationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultations'] });
    },
    onError: (error: Error) => toastError(error)
  });
};

/**
 * FOR ORDER READY
 * @param consultationId
 * @returns
 */
export const confirmOrder = async (orderId: string) => {
  const { data: result } = await api.patch(`${API_URL}/orders/${orderId}/ready`);
  return result;
};

export const useConfirmOrder = () => {
  return useMutation({
    mutationFn: (orderId: string) => confirmOrder(orderId),
    onError: (error: Error) => toastError(error)
  });
};

/**
 * FOR MARK AS PAID
 * @param orderId
 * @returns
 */
export const orderPaid = async (orderId: string) => {
  const { data: result } = await api.patch(`${API_URL}/orders/${orderId}/paid`);
  return result;
};

export const useOrderPaid = () => {
  return useMutation({
    mutationFn: (orderId: string) => orderPaid(orderId),
    onError: (error: Error) => toastError(error)
  });
};

/**
 * FOR BULK ORDERS READY
 * @param selectedId
 * @returns
 */
export const confirmBulkOrder = async (selectedId: string[]) => {
  const { data: result } = await api.post(`${API_URL}/order/confirm/bulk`, {
    id: selectedId
  });

  return result;
};

export const useConfirmBulkOrder = () => {
  return useMutation({
    mutationFn: (selectedId: string[]) => confirmBulkOrder(selectedId),
    onError: (error: Error) => toastError(error)
  });
};

/**
 * GET PRODUCTS
 * @param param0
 * @returns
 */
export const getProducts = async ({
  page,
  perPage,
  isActive,
  search
}: IGetProductProp): Promise<IEscriptProductsResponse> => {
  let url = `${API_URL}/products?page=${page}&perPage=${perPage}`;

  if (isActive) url += '&status=ACTIVE';
  if (search) url += `&search=${search}`;

  const { data }: { data: IEscriptProductsResponse } = await api.get(url);

  return data;
};

export const useGetProducts = ({ page, perPage, isActive = false, search }: IGetProductProp) => {
  return useQuery({
    queryKey: ['escriptsProducts', { page, isActive, search }],
    queryFn: () => getProducts({ page, perPage, isActive, search }),
    initialData: { message: null, status: 200, count: 0, data: [] }
  });
};

/**
 * GET PRODUCTS
 * @param param0
 * @returns
 */
export const getSupplements = async ({
  page,
  perPage,
  isActive,
  search
}: IGetSupplementsProp): Promise<IEscriptSupplementsResponse> => {
  let url = `${API_URL}/supplements?page=${page}&perPage=${perPage}`;

  if (isActive) url += '&status=ACTIVE';
  if (search) url += `&search=${search}`;

  const { data }: { data: IEscriptSupplementsResponse } = await api.get(url);

  return data;
};

export const useGetSupplements = ({
  page,
  perPage,
  isActive = false,
  search
}: IGetSupplementsProp) => {
  return useQuery({
    queryKey: ['escriptsSupplements', { page, isActive, search }],
    queryFn: () => getSupplements({ page, perPage, isActive, search }),
    initialData: { message: null, status: 200, count: 0, data: [] }
  });
};

/**
 * ORDER HISTORY
 * @param param0
 * @returns
 */
export const getOrderHistory = async ({
  page,
  perPage,
  search,
  date
}: IEscriptParamProp): Promise<IEscriptOrdersHistoryResponse> => {
  const searchStart = date.from ? calendarDate({ date: date.from }).fromUtc : null;
  const searchEnd = date.to ? calendarDate({ date: date.to }).toUtc : null;

  let apiUrl = `${API_URL}/history?page=${page}&perPage=${perPage}`;

  if (searchStart && searchEnd) apiUrl += `&dateFrom=${searchStart}&dateTo=${searchEnd}`;
  if (search) apiUrl += `&search=${search}`;

  const { data } = await api.get(apiUrl);

  return data;
};

export const useGetOrderHistory = ({ page, perPage, date, search }: IEscriptParamProp) => {
  return useQuery({
    queryKey: ['escriptsOrderHistory', { page, perPage, date, search }],
    queryFn: () => getOrderHistory({ page, perPage, date, search }),
    initialData: { message: null, status: 200, count: 0, data: [] }
  });
};
interface AddToCartInput {
  patient_id: string;
  product_id?: string;
  supplement_id?: string;
  cart_type: "PRESCRIPTION" | "SUPPLEMENT";
  quantity: number;
}

/**
 * ADDING TO CART
 * @param payload
 * @returns
 */
export const updateCart = async (payload: AddToCartInput) => {
  const { data: result } = await api.post(`${API_URL}/${payload.patient_id}/cart`, payload);
  return result;
};

export const useUpdatecart = () => {
  return useMutation({
    mutationFn: (payload: AddToCartInput) =>
      updateCart(payload),
    onError: (error: Error) => toastError(error)
  });
};

/**
 * REMOVE ITEM FROM CART
 * @param id
 * @returns
 */
export const removeItemCart = async (id: string) => {
  const { data: result } = await api.delete(`${API_URL}/cart/${id}`);
  return result;
};

export const useRemoveItemCart = () => {
  return useMutation({
    mutationFn: (id: string) => removeItemCart(id),
    onError: (error: Error) => toastError(error)
  });
};

/**
 * GET ITEM CART
 * @param param0
 * @returns
 */
export const getCartItems = async ({
  patientId
}: {
  patientId: string;
}): Promise<IEscriptCartResponse> => {
  const { data } = await api.get(`${API_URL}/${patientId}/cart`);
  return data;
};

export const useGetCartItems = ({ patientId }: { patientId: string }) => {
  return useQuery({
    queryKey: ['escriptsCartItems', { patientId }],
    queryFn: () => getCartItems({ patientId }),
    initialData: { message: null, status: 200, count: 0, cart: [], price: 0 }
  });
};

/**
 * CREATE ORDER
 * @param id
 * @returns
 */
export const createOrder = async (data: IEscriptCheckoutProp) => {
  const { data: result } = await api.post(`${API_URL}/orders`, {
    patient_id: data.patient_id,
    processPayment: data.processPayment,
    source_id: data.source_id,
    items: data.items
  });

  return result;
};

export const useCreateOrder = () => {
  return useMutation({
    mutationFn: (data: IEscriptCheckoutProp) => createOrder(data),
    onError: (error: Error) => toastError(error)
  });
};

/**
 * GET OPEN ORDERS
 * @param param0
 * @returns
 */
export const getOrderDetails = async ({
  orderId,
  withRefund
}: {
  orderId: string;
  withRefund?: {
    isRefunded: boolean;
  };
}): Promise<IEscriptOrderDataResponse> => {
  const { data } = await api.get(`${API_URL}/orders/${orderId}`);

  return data;
};

export const useGetOrderDetails = ({
  orderId,
  withRefund
}: {
  orderId: string;
  withRefund?: {
    isRefunded: boolean;
  };
}) => {
  return useQuery({
    queryKey: ['getOrder', { orderId, withRefund }],
    queryFn: () => getOrderDetails({ orderId, withRefund }),
    initialData: { message: null, status: 200, count: 0, data: null }
  });
};

/**
 * AMEND ORDER
 * @param id
 * @returns
 */
export const amendOrder = async (data: IEscriptAmendOrderProp) => {
  const { data: result } = await api.patch(`${API_URL}/orders/${data.order_id}`, data);

  return result;
};

export const useAmendOrder = () => {
  return useMutation({
    mutationFn: (data: IEscriptAmendOrderProp) => amendOrder(data),
    onError: (error: Error) => toastError(error)
  });
};

/**
 * GET PATIENT ORDER HISTORY
 * @param id
 * @returns
 */
export const getPatientOrderHistory = async (id: string): Promise<IOrderHistoryResponse> => {
  const { data } = await api.get(`${API_URL}/${id}/orders`);
  return data;
};

export const useGetPatientOrderHistory = ({ id }: { id: string }) => {
  return useQuery({
    queryKey: ['getPatientOrderHistory', { id }],
    queryFn: () => getPatientOrderHistory(id),
    onError: (error) => toastError(error),
    initialData: { message: null, status: 200, count: 0, data: [] }
  });
};

/**
 * GET PATIENT ORDER CART
 * @param id
 * @returns
 */

export const getPatientCart = async (id: string): Promise<IPatientCart> => {
  const { data } = await api.get(`${API_URL}/${id}/cart`);
  return data;
};

export const useGetPatientCart = ({ id }: { id: string }) => {
  return useQuery<IPatientCart, Error>(['getPatientCart', id], () => getPatientCart(id), {
    onError: (error: Error) => toastError(error),
    initialData: { cart: [] }
  });
};

/**
 * REFUND AMEND ORDER
 * @param id
 * @returns
 */
export const refundAmendOrder = async (data: IEscriptAmendOrderProp) => {
  const { data: result } = await api.patch(`${API_URL}/orders/${data.order_id}/refund`, data);

  return result;
};

export const useRefundAmendOrder = () => {
  return useMutation({
    mutationFn: (data: IEscriptAmendOrderProp) => refundAmendOrder(data),
    onError: (error: Error) => toastError(error)
  });
};

/**
 * GET SCRIPT PRODUCT
 * @param param0
 * @returns
 */
export const getScriptProduct = async ({
  scriptProductId
}: {
  scriptProductId: string;
}): Promise<IEscriptScriptProductsResponse> => {
  const { data } = await api.get(`${API_URL}/${scriptProductId}/product`);
  return data;
};

export const useGetScriptProduct = ({ scriptProductId }: { scriptProductId: string }) => {
  return useQuery({
    queryKey: ['getScriptProduct', { scriptProductId }],
    queryFn: () => getScriptProduct({ scriptProductId })
  });
};

/**
 * print order
 * @returns
 */
export const printOrder = async (id: string[]): Promise<IPrintOrder> => {
  const { data } = await api.post(`${API_URL}/orders/shipments`, {
    order_ids: id
  });

  return data;
};

export const usePrintOrder = () => {
  return useMutation({
    mutationFn: (id: string[]) => printOrder(id),
    onError: (error: Error) => toastError(error)
  });
};

/**
 * GET PATIENT ESCRIPTS
 * @param param0
 * @returns
 */
export const getPatientScripts = async ({
  patientId,
  isEscriptPatientId
}: {
  patientId: string;
  isEscriptPatientId?: '0' | '1';
}): Promise<IPatientEscriptsResponse> => {
  const { data } = await api.get(
    `${API_URL}/${patientId}?isEscriptPatientId=${isEscriptPatientId}`
  );
  return data;
};

export const useGetPatientScripts = ({
  patientId,
  isEscriptPatientId
}: {
  patientId: string;
  isEscriptPatientId?: '0' | '1';
}) => {
  return useQuery({
    queryKey: ['getPatientScripts', { patientId }],
    queryFn: () => getPatientScripts({ patientId, isEscriptPatientId })
  });
};

export const getEscripts = async ({
  patientId,
  isHistory = 0,
  status
}: {
  patientId: string;
  isHistory?: 0 | 1;
  status: string;
}): Promise<{
  prescriptions: IEscriptPrescription[];
  supplements: IEscriptSupplement[];
  lifestyles: IEscriptLifestyle[];
}> => {
    const params = new URLSearchParams({ status });

    if (isHistory === 1) {
      params.append('isHistory', '1');
    }

    const { data } = await api.get(`${API_URL}/${patientId}?${params.toString()}`);
    return data;
};

export const useGetEscripts = ({
  patientId,
  isHistory = 0,
  status
}: {
  patientId: string;
  isHistory?: 0 | 1;
  status: string;
}) => {
  return useQuery({
    queryKey: ['escripts', { patientId, isHistory }],
    queryFn: () => getEscripts({ patientId, isHistory, status: status }),
    enabled: patientId !== '',
    initialData: {
      prescriptions: [],
      supplements: [],
      lifestyles: []
    }
  });
};

export const voidEscript = async ({
  type,
  script_id
}: {
  type: 'PRODUCT' | 'SUPPLEMENT';
  script_id: string;
}) => {
  const resp = await api.delete(`${API_URL}/void`, {
    data: {
      type,
      script_id
    }
  });

  return resp;
};

export const useVoidEscript = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ...payload }: { type: 'PRODUCT' | 'SUPPLEMENT'; script_id: string }) =>
      voidEscript({ ...payload }),
    onError: (error: Error) => toastError(error),
    onSuccess: (resp) => {
      if (resp.data.status === 200) {
        toast({
          title: 'E-Script has been void',
          variant: 'success'
        });
        queryClient.invalidateQueries({ queryKey: ['escripts'] });
      }
    }
  });
};

// getOrder

// status
export const getOrderHeaderNumbers = async (): Promise<{
  data: IOrderHeaderData;
  dataEntries: IOrderHeaderDataEntries[];
}> => {
  const {
    data: { data }
  }: {
    data: {
      data: IOrderHeaderData;
    };
  } = await api.get(`${API_URL}/orders/summary`);

  const dataEntries = Object.entries(data).map((item) => ({ name: item[0], count: item[1] }));
  dataEntries.push({ name: 'processed', count: null });
  dataEntries.push({ name: 'cancelled', count: null });

  return { data, dataEntries };
};

export const useGetOrderHeaderNumbers = () => {
  return useQuery({
    queryKey: ['orderHeaderNumbers'],
    queryFn: () => getOrderHeaderNumbers(),
    initialData: {
      data: {
        orders: 0,
        review: 0,
        dispensing: 0,
        shipping: 0,
        processed: 0
      },
      dataEntries: []
    },
    onError: (error) => toastError(error)
  });
};

// review order
export const reviewOrder = async (id: string) => {
  const { data: result } = await api.patch(`${API_URL}/orders/${id}/review`);
  // if (search) apiUrl += `&search=${search}`;
  return result;
};

export const useReviewOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reviewOrder(id),
    onError: (error) => toastError(error),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderHeaderNumbers'] });
    }
  });
};

export const dispensingOrder = async ({
  consultationId,
  payload
}: {
  consultationId: string;
  payload: {
    delivery_address: string;
    delivery_city: string;
    delivery_state: string;
    delivery_postcode: string;
    pharmacy_id: string;
  };
}) => {
  const { data: result } = await api.patch(
    `${API_URL}/orders/${consultationId}/dispensing`,
    payload
  );
  return result;
};

export const useDispensingOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      ...payload
    }: {
      consultationId: string;
      payload: {
        delivery_address: string;
        delivery_city: string;
        delivery_state: string;
        delivery_postcode: string;
        pharmacy_id: string;
      };
    }) => dispensingOrder({ ...payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderHeaderNumbers'] });
    },
    onError: (error: Error) => toastError(error)
  });
};

export const getProcessedOrder = async ({
  search
}: {
  search: string;
}): Promise<IOrderHistoryResponse> => {
  let apiUrl = `${API_URL}/orders`;
  if (search) apiUrl += `?search=${search}`;

  const { data } = await api.get(apiUrl);
  return data;
};

export const useGetProcessedOrder = ({ search }: { search: string }) => {
  return useQuery({
    queryKey: ['getProcessedOrder'],
    queryFn: () => getProcessedOrder({ search }),
    onError: (error) => toastError(error),
    initialData: { message: null, status: 200, count: 0, data: [] }
  });
};

export const markShipped = async (id: string) => {
  const { data: result } = await api.patch(`${API_URL}/order/${id}/shipped`);
  return result;
};

export const useMarkShipped = () => {
  return useMutation({
    mutationFn: (id: string) => markShipped(id),
    onError: (error) => toastError(error)
  });
};

export const getPharmacies = async (): Promise<{
  data: IPharmacy[];
  options: ISelectOptions[];
}> => {
  const {
    data: { data }
  }: {
    data: {
      data: IPharmacy[];
    };
  } = await api.get(`${API_URL}/pharmacies`);

  return {
    data,
    options: data.map((item) => ({ label: item.name, value: item.id }))
  };
};

export const useGetPharmacies = () => {
  return useQuery({
    queryKey: ['escriptsPharmacies'],
    queryFn: () => getPharmacies(),
    initialData: {
      data: [],
      options: []
    }
  });
};

export const printLabel = async ({
  orderId,
  pharmacy_id
}: {
  orderId: string;
  pharmacy_id: string;
}): Promise<any> => {
  const { data } = await api.get(`${API_URL}/order/${orderId}/label?pharmacy_id=${pharmacy_id}`);

  return data;
};

export const useGetPrintLabel = ({
  orderId,
  pharmacy_id
}: {
  orderId: string;
  pharmacy_id: string;
}) => {
  return useQuery({
    queryKey: ['escriptPrintLabel', { orderId }],
    queryFn: () => printLabel({ orderId, pharmacy_id }),
    initialData: { message: null, status: 200, count: 0, data: [] }
  });
};


/**
 * CREATE INVOICE
 * @param param0
 * @returns
 */
export const createOrderInvoice = async (payload: TCreateOrderInvoice): Promise<any> => {
  const { data: result } = await api.post(`${API_URL}/orders/create-invoice`, payload);
  return result;
};

export const useCreateOrderInvoice = () => {
  return useMutation({
    mutationFn: ({ payload }: { payload: TCreateOrderInvoice }) => createOrderInvoice(payload),
    onError: (error: any) => toastError(error)
  });
};